import { t } from 'i18next'
import { object, array, date, number } from 'yup'
import { WallboxTariffsFormData } from 'src/Libs/e-mobility/emobility-wallbox'
import { FormInitializerReturnType } from 'src/Configuration/validation'

/**
 * Initializes the form for editing an e-mobility wallboxTariff.
 * @param wallboxTariffData  - WallboxTariffsFormData
 * @returns initialValues and validationSchema for EmobilityWallbox form
 */
export const useEmobilityWallboxTariffsFormInitializer = (
  wallboxTariffData: WallboxTariffsFormData
): FormInitializerReturnType<WallboxTariffsFormData, WallboxTariffsFormData> => {
  const initialValues: WallboxTariffsFormData = {
    chargePrices: wallboxTariffData.chargePrices,
    dischargePrices: wallboxTariffData.dischargePrices,
  }

  const validationSchema = object().shape({
    chargePrices: array()
      .required()
      .of(
        object().shape({
          validFrom: date()
            .typeError(t('emobility.wallbox.editTariffs.validDateFormat'))
            .nullable()
            .required(t('emobility.wallbox.editTariffs.dateRequired')),
          price: number().required(t('emobility.wallbox.editTariffs.priceRequired')).positive(t('emobility.wallbox.editTariffs.mustBePositiveValue')),
        })
      )
      .test('unique-dates', function (values) {
        const validFromValues = values?.map(value => value.validFrom?.getTime())
        const uniqueValues = new Set(validFromValues)

        if (validFromValues) {
          for (const [index, elem] of validFromValues.entries()) {
            if (validFromValues.indexOf(elem) !== index) {
              throw this.createError({ message: t('emobility.wallbox.editTariffs.duplicateDate'), path: `chargePrices[${index}].validFrom` })
            }
          }
        }

        return uniqueValues.size === validFromValues?.length
      }),
    dischargePrices: array()
      .required()
      .of(
        object().shape({
          validFrom: date()
            .typeError(t('emobility.wallbox.editTariffs.validDateFormat'))
            .nullable()
            .required(t('emobility.wallbox.editTariffs.dateRequired')),
          price: number()
            .required(t('emobility.wallbox.editTariffs.priceRequired'))
            .test('is-number-positive', t('emobility.wallbox.editTariffs.mustBePositiveValue'), value => {
              if (!value) {
                return true
              }

              return value >= 0
            }),
        })
      )
      .test('unique-dates', function (values) {
        const validFromValues = values?.map(value => value.validFrom?.getTime())
        const uniqueValues = new Set(validFromValues)

        if (validFromValues) {
          for (const [index, elem] of validFromValues.entries()) {
            if (validFromValues.indexOf(elem) !== index) {
              throw this.createError({ message: t('emobility.wallbox.editTariffs.duplicateDate'), path: `dischargePrices[${index}].validFrom` })
            }
          }
        }

        return uniqueValues.size === validFromValues?.length
      }),
  })

  return { initialValues, validationSchema }
}
